import Button from "@common/controls/Button/component";
import ButtonsContainer from "@common/controls/Container/Buttons";
import Icon from "@common/controls/Icon/component";
import { IconType, type InputTypeProps } from "@common/declarations";
import withDropdownHOC, { type WithDropdownCmpProps, type WithDropdownToggleProps } from "@common/hocs/withDropdown";
import { ContentAlign } from "@common/hocs/withDropdown/lib";
import withValueHOC from "@common/hocs/withValue";
import { useI18n } from "@common/hooks/i18n";
import loadable from "@loadable/component";
import classnames from "classnames";
import defaultLocale from "date-fns/locale/en-US";
import * as React from "react";
import { type DateRangeProps, type Range, type RangeKeyDict } from "react-date-range";
import { defaultInputRanges, defaultStaticRanges, formatDisplayValue, parseValue, toInput, toOutput } from "./lib";

const DR = loadable(() => import(/* webpackChunkName: "react-date-range" */ "react-date-range"), {
  fallback: <div style={{ padding: 16 }}>Loading...</div>,
  resolveComponent: (x) => x.DateRange
});

const DRP = loadable(() => import(/* webpackChunkName: "react-date-range" */ "react-date-range"), {
  fallback: <div style={{ padding: 16 }}>Loading...</div>,
  resolveComponent: (x) => x.DateRangePicker
});

export type DateRangeValue = {
  from: Date | null;
  to: Date | null;
};

export type DateRangePickerMode = "dateRangePicker" | "dateRange";

type DateRangePickerPropsInner = InputTypeProps<DateRangeValue | string | null> & {
  chooseMode?: "button" | "select";
  className?: string;
  isDisabled?: boolean;
  onApply?: (value: DateRangeValue | null) => Promise<void> | void;
  maxDate?: Date;
  minDate?: Date;
  mode?: DateRangePickerMode;
  outputType?: "object" | "string";
  style?: React.CSSProperties;
};

const DateRangePickerToggle = React.memo((props: WithDropdownToggleProps & DateRangePickerPropsInner) => {
  const i18n = useI18n();
  return (
    <div
      ref={props.toggleRef}
      className={classnames("date_range_picker_toggle", props.className, {
        open: props.isOpen,
        disabled: props.isDisabled
      })}
      onClick={props.isDisabled ? undefined : props.toggleOpen}
      style={props.style}
      tabIndex={0}
      data-name={props.qa || props.name}
    >
      <Icon icon={IconType.CalendarWeek} />
      <span className="value single-line-ellipsis">{formatDisplayValue(i18n, props.value)}</span>
      {!!props.value && (
        <Icon className="clear" icon={IconType.Disabled} onClick={(e) => (e.stopPropagation(), props.onChange(null))} />
      )}
    </div>
  );
});

export const DateRangePickerContent = React.forwardRef(
  (
    { chooseMode = "button", ...props }: WithDropdownCmpProps & DateRangePickerPropsInner,
    ref: React.RefObject<HTMLDivElement>
  ) => {
    const i18n = useI18n();

    const [value, setValue] = React.useState<DateRangeValue | null>(parseValue(props.value));

    React.useEffect(() => setValue(parseValue(props.value)), [props.value]);

    const onChangeInner = React.useCallback(
      (value: DateRangeValue | null) => {
        let finalValue: DateRangeValue | string | null = value;
        if (value && props.outputType === "string") {
          finalValue = JSON.stringify(value);
        }

        props.onChange(finalValue);
      },
      [props.onChange, props.outputType]
    );

    const onApplyClick = React.useCallback(async () => {
      onChangeInner(value);

      if (props.onApply) {
        await props.onApply(value);
      }
      props.close();
    }, [props.close, onChangeInner, value]);

    const onChange = React.useCallback(
      (range: RangeKeyDict): void => {
        const r = range["selection"] as Range;
        const value = {
          from: toOutput(r.startDate, true),
          to: toOutput(r.endDate, false)
        };
        setValue(value);

        chooseMode === "select" && onChangeInner(value);
      },
      [onChangeInner, chooseMode, setValue]
    );

    const startDate = React.useMemo(() => toInput(value?.from ?? new Date())!, [value]);
    const endDate = React.useMemo(() => toInput(value?.to ?? new Date())!, [value]);

    const baseProps = React.useMemo<DateRangeProps>(
      () => ({
        minDate: props.minDate,
        maxDate: props.maxDate,
        dateDisplayFormat: "yyyy MMM dd",
        locale: defaultLocale,
        rangeColors: ["var(--color)"],
        onChange: onChange,
        ranges: [
          {
            startDate,
            endDate: endDate < startDate ? startDate : endDate,
            key: "selection"
          }
        ]
      }),
      [props.minDate, props.maxDate, onChange, startDate, endDate]
    );

    return (
      <div ref={ref} className="content">
        {props.mode === "dateRange" ? (
          <DR {...baseProps} />
        ) : (
          <DRP {...baseProps} inputRanges={defaultInputRanges} staticRanges={defaultStaticRanges} />
        )}

        {chooseMode === "button" && (
          <ButtonsContainer>
            <Button onClick={props.close} type="white">
              {i18n.cancel}
            </Button>
            <Button onClick={onApplyClick}>{i18n.apply}</Button>
          </ButtonsContainer>
        )}
      </div>
    );
  }
);

export type DateRangePickerProps = DateRangePickerPropsInner;
const DateRangePickerDropdown = withDropdownHOC<DateRangePickerProps>(DateRangePickerToggle, DateRangePickerContent);
const DateRangePickerBase = React.memo((props: Omit<DateRangePickerProps, "dropdownClassName">) => (
  <DateRangePickerDropdown
    {...props}
    align={ContentAlign.Left}
    dropdownClassName="date_range_picker_dropdown"
    maxHeight="unset"
  />
));

const DateRangePicker = withValueHOC<DateRangeValue | string | null, DateRangePickerProps>(DateRangePickerBase);
export default DateRangePicker;
